import { useTranslation } from 'next-i18next/pages';
import React from 'react';

import useCurrentLang from '@/hooks/apps/useCurrentLang';

import { type PartnerMark, selectPartnerMarks } from '@/data/shared/partners';

import Area from '@/components/apps/layouts/shared/Area';
import AboutPartnerRow from '@/components/apps/pages/about/AboutPartnerRow';
import SectionTitle from '@/components/apps/ui/title/SectionTitle';
import TransCustom from '@/components/apps/ui/transCustom/TransCustom';

/** How many bands the wall is stacked in. */
const BAND_COUNT = 3;

/**
 * The fewest marks a band carries. A band loops by wrapping over the width
 * of one copy of its marks, so a copy narrower than the screen would leave
 * a gap at the wrap; a short band repeats its marks until it is wide enough
 * that no screen sees the seam.
 */
const MIN_MARKS_PER_BAND = 8;

/**
 * Deals the marks into bands one at a time, so neighbouring marks land on
 * different bands and each band is a cross-section of the whole list
 * rather than a run of one group.
 */
function dealIntoBands(
  marks: ReadonlyArray<PartnerMark>,
  count: number,
): Array<Array<PartnerMark>> {
  const bands: Array<Array<PartnerMark>> = Array.from(
    { length: count },
    () => [],
  );
  marks.forEach((mark, index) => {
    bands[index % count]?.push(mark);
  });

  return bands;
}

/** Repeats a short band's marks until it carries at least `minimum`, with keys kept distinct. */
function padToMinimum(
  marks: ReadonlyArray<PartnerMark>,
  minimum: number,
): Array<PartnerMark> {
  if (marks.length === 0) return [];
  const padded: Array<PartnerMark> = [];
  for (let pass = 0; padded.length < minimum; pass += 1) {
    for (const mark of marks) {
      padded.push(pass === 0 ? mark : { ...mark, id: `${mark.id}-${pass}` });
    }
  }

  return padded;
}

/**
 * ### Partners
 *
 * The wall of marks, read from the one list the About page's bands are read
 * from, and dealt into three bands that drift in alternating directions.
 * The list used to be written out here as filenames, which is how the two
 * pages came to disagree about who the company has worked with.
 *
 * @returns The rendered section
 * @see {@link selectPartnerMarks} The marks rendered here
 */
export default function Partners() {
  const { t } = useTranslation('common');
  const currentLang = useCurrentLang();
  const bands = dealIntoBands(selectPartnerMarks(currentLang), BAND_COUNT);

  return (
    <div className="bg-apps-coreGrayWhite py-[100px] lg:py-[clamp(100px,14.286vw,200px)]">
      <Area>
        <SectionTitle
          title={t('기술 혁신을 이끌어나가는 기업사')}
          description={
            <TransCustom i18nKey="디플리는 글로벌 수준의 Tech 기업, 공기업과 관계를 맺고 기술의 가치를 세상에 전달하는 걸음을 함께하고 있어요." />
          }
        />
        <div className="mt-[24px] flex flex-col lg:mt-[clamp(24px,4vw,48px)]">
          {bands.map((members, index) => (
            <AboutPartnerRow
              key={index}
              group={{
                id: 'collaboration',
                title: '',
                members: padToMinimum(members, MIN_MARKS_PER_BAND),
              }}
              index={index}
              showTitle={false}
            />
          ))}
        </div>
      </Area>
    </div>
  );
}
